<script lang="ts" setup>
  import { computed, ref, type Ref } from 'vue'

  import Dialog from 'primevue/dialog'
  import { type AwaitAsyncTasksAddEvent, type AwaitAsyncTasksRemoveEvent, AwaitAsyncTasksTaskState, type Task, type TaskResult, type TaskResults } from '@/services/AwaitAsyncTasksService'
  import SpinnerDefault from '@/elements/SpinnerDefault.vue'
  import { CheckIcon, ClockIcon, XMarkIcon } from '@heroicons/vue/24/solid'

  import { useI18n } from 'vue-i18n'
  import { useEventBus } from '@vueuse/core'

  const { t } = useI18n()
  const header = ref('')

  interface InnerTaskResult extends TaskResult {
    readonly task: Task
    state: AwaitAsyncTasksTaskState
    runtime: number
  }

  const taskList: Ref<Map<string, InnerTaskResult>> = ref(new Map<string, InnerTaskResult>())

  interface MappedType {
    key: string
    value: InnerTaskResult
  }

  const taskListMapped = computed<MappedType[]>(() => {
    const mapped: { key: string; value: InnerTaskResult }[] = []
    for (const [key, value] of taskList.value.entries()) {
      mapped.push({ key: key, value: value })
    }
    return mapped
  })
  const onAdd = async (ev: AwaitAsyncTasksAddEvent): Promise<void> => {
    if (taskList.value.has(ev.name)) {
      throw Error('Task with same name is already existing')
    }
    taskList.value.set(ev.name, {
      state: AwaitAsyncTasksTaskState.ADDED,
      task: ev.task,
      runtime: 0
    })
  }
  const onRemove = (ev: AwaitAsyncTasksRemoveEvent): void => {
    if (ev.name) taskList.value.delete(ev.name)
    if (ev.task) {
      const matched: string[] = []
      for (const [key, value] of taskList.value.entries()) {
        if (value.task === ev.task) {
          matched.push(key)
        }
      }
      for (const key of matched) {
        onRemove({ name: key })
      }
    }
  }
  const onClear = (): void => {
    taskList.value.clear()
  }
  const onRun = async (newHeader?: string): Promise<void> => {
    header.value = newHeader ?? ''
    if (taskList.value.size === 0) {
      //nothing to work on - signal finished
      const d = new Date()
      await finalize(d, d)
      return
    }
    visible.value = true
  }
  useEventBus<AwaitAsyncTasksAddEvent>('awaitasynctasks.add').on(onAdd)
  useEventBus<AwaitAsyncTasksRemoveEvent>('awaitasynctasks.remove').on(onRemove)
  useEventBus<string>('awaitasynctasks.run').on(onRun)
  useEventBus('awaitasynctasks.clear').on(onClear)

  const finalize = async (startTime: Date, endTime: Date) => {
    const results: Record<string, TaskResult> = {}
    let success = true
    for (const [key, value] of taskList.value.entries()) {
      // results[key] = JSON.parse(JSON.stringify(value))
      results[key] = value
      success = success && value.error === undefined
    }
    const event: TaskResults = {
      started: startTime,
      ended: endTime,
      duration: endTime.getTime() - startTime.getTime(),
      results: results,
      success: success
    }
    setTimeout(
      () => {
        useEventBus<TaskResults>('awaitasynctasks.finish').emit(event)
        onClear()
        visible.value = false
      },
      success ? 0 : 5000
    )
  }
  //
  const visible = ref(false)
  const start = async () => {
    for (const value of taskList.value.values()) {
      value.state = AwaitAsyncTasksTaskState.PENDING
    }
    const startTime: Date = new Date()
    const tempResults: Record<string, TaskResult> = {}
    let oneFailed = false
    for (const [key, value] of taskList.value.entries()) {
      value.state = AwaitAsyncTasksTaskState.RUNNING
      value.started = new Date()
      if (oneFailed) {
        value.state = AwaitAsyncTasksTaskState.FAILED
        value.error = Error(t('Skipped due fail before'))
        value.ended = value.started
        value.duration = 0
        value.result = undefined
        continue
      }
      const taskTimer = setInterval(() => (value.runtime = new Date().getTime() - value.started!.getTime()), 100)
      let thisResult: unknown = undefined
      try {
        thisResult = value.result = await value.task(tempResults)
        value.state = AwaitAsyncTasksTaskState.SUCCESS
      } catch (e) {
        value.error = e
        value.state = AwaitAsyncTasksTaskState.FAILED
        oneFailed = true
      }
      value.ended = new Date()
      value.duration = new Date().getTime() - value.started.getTime()
      tempResults[key] = {
        started: value.started,
        ended: value.ended,
        duration: value.duration,
        result: thisResult,
        error: value.error
      }
      clearInterval(taskTimer)
    }
    const endTime = new Date()
    await finalize(startTime, endTime)
  }
  const getColorClass = (state: AwaitAsyncTasksTaskState): string => {
    switch (state) {
      case AwaitAsyncTasksTaskState.FAILED:
        return 'text-red-700'
      case AwaitAsyncTasksTaskState.PENDING:
        return 'text-gray-300'
      case AwaitAsyncTasksTaskState.SUCCESS:
        return 'text-green-700'
      case AwaitAsyncTasksTaskState.ADDED:
      case AwaitAsyncTasksTaskState.RUNNING:
      default:
        return ''
    }
  }
</script>

<template>
  <Transition
    enter-active-class="duration-300 ease-out"
    enter-from-class="transform opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="duration-200 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="transform opacity-0">
    <div v-if="visible">
      <Dialog
        :closable="false"
        :header="header"
        :visible="visible"
        class="min-w-fit"
        modal
        @show="start">
        <DataTable
          :value="taskListMapped"
          data-no-header>
          <template #header />
          <Column>
            <template #body="{ data }: { data: MappedType }">
              <span :class="['font-semibold', getColorClass(data.value.state)]">
                {{ data.key }}
              </span>
            </template>
          </Column>
          <Column body-class="pl-4">
            <template #body="{ data }: { data: MappedType }">
              <span :class="getColorClass(data.value.state)">
                <SpinnerDefault
                  v-if="data.value.state == AwaitAsyncTasksTaskState.RUNNING"
                  class="min-w-8 h-5 w-5" />
                <CheckIcon
                  v-if="data.value.state == AwaitAsyncTasksTaskState.SUCCESS"
                  class="h-5 w-5" />
                <XMarkIcon
                  v-if="data.value.state == AwaitAsyncTasksTaskState.FAILED"
                  class="h-5 w-5" />
                <ClockIcon
                  v-if="data.value.state == AwaitAsyncTasksTaskState.PENDING"
                  class="h-5 w-5" />
              </span>
            </template>
          </Column>
        </DataTable>
      </Dialog>
    </div>
  </Transition>
</template>
<i18n>
{
  "de": {
    "SUCCESS": "abgeschlossen",
    "RUNNING": "in Arbeit",
    "PENDING": "ausstehend",
    "FAILED": "fehlgeschlagen",
    "Skipped due fail before": "Übersprungen aufgrund vorheriger Fehler"
  },
  "en": {
    "SUCCESS": "Success",
    "RUNNING": "Running",
    "PENDING": "Pending",
    "FAILED": "Failed",
    "Skipped due fail before": "Skipped due past error"
  }
}
</i18n>
